import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL = '/v1'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token management
let accessToken: string | null = localStorage.getItem('access_token')
let refreshToken: string | null = localStorage.getItem('refresh_token')

export const setTokens = (access: string, refresh: string) => {
  accessToken = access
  refreshToken = refresh
  localStorage.setItem('access_token', access)
  localStorage.setItem('refresh_token', refresh)
}

export const clearTokens = () => {
  accessToken = null
  refreshToken = null
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

export const getAccessToken = () => accessToken

// Request interceptor to add auth header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry && refreshToken) {
      originalRequest._retry = true

      try {
        const response = await axios.post(`${API_BASE_URL}/operators/refresh`, {
          refresh_token: refreshToken,
        })

        const { access_token, refresh_token } = response.data
        setTokens(access_token, refresh_token)

        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return apiClient(originalRequest)
      } catch {
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/operators/login', { email, password })
    const { access_token, refresh_token, operator } = response.data
    setTokens(access_token, refresh_token)
    return { operator, access_token, refresh_token }
  },

  logout: async () => {
    try {
      await apiClient.post('/operators/logout')
    } finally {
      clearTokens()
    }
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/me')
    return response.data.operator ?? response.data
  },
}

// Devices API
export const devicesApi = {
  list: async (status?: string) => {
    const params = status ? { status } : {}
    const response = await apiClient.get('/devices', { params })
    return response.data
  },

  get: async (id: string) => {
    const response = await apiClient.get(`/devices/${id}`)
    return response.data
  },

  getInstallation: async (id: string) => {
    const response = await apiClient.get(`/devices/${id}/installation`)
    return response.data
  },

  update: async (id: string, data: { name: string }) => {
    const response = await apiClient.put(`/devices/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    await apiClient.delete(`/devices/${id}`)
  },

  revoke: async (id: string) => {
    const response = await apiClient.post(`/devices/${id}/revoke`)
    return response.data
  },

  listPending: async () => {
    const response = await apiClient.get('/devices/pending')
    return response.data
  },

  claim: async (code: string) => {
    const response = await apiClient.post(`/devices/claim/${code}`)
    return response.data
  },
}

// Tunnels API
export const tunnelsApi = {
  list: async (deviceId?: string) => {
    const params = deviceId ? { device_id: deviceId } : {}
    const response = await apiClient.get('/tunnels', { params })
    return response.data
  },

  get: async (id: string) => {
    const response = await apiClient.get(`/tunnels/${id}`)
    return response.data
  },

  create: async (data: {
    device_id: string
    name: string
    mode: string
    target_addr?: string
    target_port: number
    fallback?: string
    public_port?: number
    domain?: string
    relay_id?: string
  }) => {
    const response = await apiClient.post('/tunnels', data)
    return response.data
  },

  update: async (id: string, data: Partial<{
    name: string
    target_addr: string
    target_port: number
    public_port: number
    domain: string
    relay_id: string
  }>) => {
    const response = await apiClient.put(`/tunnels/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    await apiClient.delete(`/tunnels/${id}`)
  },

  enable: async (id: string) => {
    const response = await apiClient.post(`/tunnels/${id}/enable`)
    return response.data
  },

  disable: async (id: string) => {
    const response = await apiClient.post(`/tunnels/${id}/disable`)
    return response.data
  },
}

// Leases API
export const leasesApi = {
  issue: async (tunnelId: string) => {
    const response = await apiClient.post('/leases/issue', { tunnel_id: tunnelId })
    return response.data
  },

  revoke: async (id: string) => {
    const response = await apiClient.post(`/leases/revoke/${id}`)
    return response.data
  },

  get: async (id: string) => {
    const response = await apiClient.get(`/leases/${id}`)
    return response.data
  },
}

// Stats API
export const statsApi = {
  getDashboard: async () => {
    const response = await apiClient.get('/stats')
    return response.data
  },
}

// Audit API
export const auditApi = {
  list: async (limit = 50, offset = 0) => {
    const response = await apiClient.get('/audit', { params: { limit, offset } })
    return response.data
  },
}

// Operators API
export const operatorsApi = {
  updatePassword: async (id: string, password: string) => {
    const response = await apiClient.put(`/operators/${id}`, { password })
    return response.data
  },

  updateProfile: async (id: string, data: { name?: string }) => {
    const response = await apiClient.put(`/operators/${id}`, data)
    return response.data
  },
}

// Relays API (admin only)
export const relaysApi = {
  list: async (status?: string, publicOnly?: boolean) => {
    const params: Record<string, string> = {}
    if (status) params.status = status
    if (publicOnly) params.public = 'true'
    const response = await apiClient.get('/relays', { params })
    return response.data
  },

  get: async (id: string) => {
    const response = await apiClient.get(`/relays/${id}`)
    return response.data
  },

  getDefault: async () => {
    const response = await apiClient.get('/relays/default')
    return response.data.relay ?? response.data
  },

  create: async (data: {
    name: string
    address: string
    region?: string
    is_default?: boolean
    is_public?: boolean
    metadata?: Record<string, unknown>
  }) => {
    const response = await apiClient.post('/relays', data)
    return response.data.relay ?? response.data
  },

  update: async (id: string, data: Partial<{
    name: string
    address: string
    region: string
    is_default: boolean
    is_public: boolean
    status: string
    status_message: string
    metadata: Record<string, unknown>
  }>) => {
    const response = await apiClient.put(`/relays/${id}`, data)
    return response.data.relay ?? response.data
  },

  delete: async (id: string) => {
    await apiClient.delete(`/relays/${id}`)
  },

  setDefault: async (id: string) => {
    const response = await apiClient.post(`/relays/${id}/default`)
    return response.data
  },
}
